/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useState, useEffect, useCallback } from 'react';
import usePrevious from '@/utils/usePrevious';
import Quantity from '@/components/AZCustomComponent/QuantityComponent';
import { cartConstants } from '@/constants/cart';
import azCommonStyles from '@/theme/globals.module.scss';
import { callToCertona } from '@/features/certona/api/callToCertona';
import { certonaPageType } from '@/constants/certonaPageType';
import { trackCartRemoval } from '@/components/Cart/analytics/trackCartRemoval';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import styles from '@/components/Cart/CommonCounterPart/PreOrderQuantitySelector/styles.module.scss';
import type { RemoveCartAnalyticsData } from '@/types/analytics';
import { CalmBoxStepper } from '@/features/cart/components/QuantityStepper';
import { calculateItemUnitPrice } from '@/components/Cart/CommonCounterPart/PreOrderQuantitySelector/utils';
import type { FunctionalComponentProps } from '@/components/Cart/CommonCounterPart/PreOrderQuantitySelector/types';
import { MiniCartError } from '@/components/Cart/CommonCounterPart/PreOrderQuantitySelector/FunctionalComponentVersion/MiniCartError';
import { EachItemPrice } from '@/components/Cart/CommonCounterPart/PreOrderQuantitySelector/FunctionalComponentVersion/EachItemPrice';

const PreOrderV2 = (props: FunctionalComponentProps) => {
  const {
    data,
    availabilityCheck,
    fulfillmentId,
    isMini,
    labels,
    updatedValue,
    updateQuantityErrorItem,
    updateQuantityError,
    disableRemoveButton,
    setFulfillmentId,
    sisterStoreAvailable,
    isSisterStoreFlyout,
    updateTotalQuantity,
    handleQuantityChange,
    updateRequestingOrderItem,
    discountedStrikeThroughPrice,
    hasDealId,
    productTotalPrice,
    strikeThroughPricingEnabled,
    filterproduct,
    isBopusMexicoStoreEnabled,
  } = props;

  const [count, setCount] = useState(data.itemQuantity);
  const [isIncOrDecBtnTouched, setIsIncOrDecBtnTouched] = useState(false);
  const [quantityEntered, setQuantityEntered] = useState(data.itemQuantity);

  const prevProps = usePrevious(props);

  const selectedFulfilmentQuantity = useCallback(
    (
      id: number,
      bopusQuantity: number,
      sthQuantity: number,
      sddQuantity: number,
      nddQuantity: number,
    ) => {
      switch (true) {
        case id === FULFILLMENT_METHODS.SAMEDAY:
          return sddQuantity;
        case id === FULFILLMENT_METHODS.NEXTDAY:
          if (data.itemQuantity > nddQuantity) {
            return sthQuantity;
          } else {
            return nddQuantity;
          }
        case id === FULFILLMENT_METHODS.ONLINEORDER:
          return sthQuantity;
        case id === FULFILLMENT_METHODS.STOREORDER:
          return bopusQuantity;
        default:
          return bopusQuantity;
      }
    },
    [data.itemQuantity],
  );

  const maxQuantityCheck = useCallback(
    (fulfilmentChanged: boolean = false, newFulfillmentId?: number) => {
      const bopusQuantity = availabilityCheck.bopusQuantity ?? 0;
      const sthQuantity = availabilityCheck.sthQuantity ?? 0;
      const sddQuantity = availabilityCheck.sddQuantity ?? 0;
      const nddQuantity = availabilityCheck.nddQuantity ?? 0;
      const updatedFulfillmentId =
        fulfilmentChanged && newFulfillmentId ? newFulfillmentId : fulfillmentId!;
      const maxQuantity = selectedFulfilmentQuantity(
        updatedFulfillmentId,
        bopusQuantity,
        sthQuantity,
        sddQuantity,
        nddQuantity,
      );

      return maxQuantity;
    },
    [availabilityCheck, fulfillmentId, selectedFulfilmentQuantity],
  );

  const quantityValidityCheck = useCallback(() => {
    const nddQuantity = availabilityCheck.nddQuantity ?? 0;
    const { itemQuantity, orderType, eligibleForNextDay } = data;
    const isOnlineOrder = orderType === cartConstants.ONLINEORDER;
    const nddQuantityCheck = eligibleForNextDay || nddQuantity > 0;

    if (!isMini && itemQuantity > maxQuantityCheck() && !isOnlineOrder) {
      updateQuantityErrorItem?.(true);
    } else if (!isMini && isOnlineOrder && nddQuantityCheck && itemQuantity > nddQuantity) {
      updateQuantityErrorItem?.(false, true);
    } else {
      updateQuantityErrorItem?.(false, false);
    }
  }, [availabilityCheck, isMini, data, updateQuantityErrorItem, maxQuantityCheck]);

  const quantityErrorCheck = useCallback(
    (checkQuantity: boolean, quantity: number) => {
      if (!isMini && quantity > 0) {
        updateQuantityError?.(checkQuantity);
        updateQuantityErrorItem?.(checkQuantity, false);
      } else if (!isMini) {
        updateQuantityError?.(checkQuantity);
        updateQuantityErrorItem?.(checkQuantity, false);
      } else if (count) {
        updatedValue?.(count);
      }
    },
    [isMini, updateQuantityError, updateQuantityErrorItem, updatedValue, count],
  );

  const handleChange = useCallback(
    (quantity: number, _analyticRequire: boolean, _showMaxQtyError?: boolean) => {
      const nddQuantity = availabilityCheck.nddQuantity;
      const sthQuantity = availabilityCheck.sthQuantity;
      const isNddAvailable = nddQuantity > 0;
      let maxQuantity = 0;
      let fulfillmentIdforQtyChange;

      if (
        quantity >= 0 &&
        isNddAvailable &&
        fulfillmentId === FULFILLMENT_METHODS.ONLINEORDER &&
        nddQuantity >= quantity
      ) {
        setFulfillmentId?.(FULFILLMENT_METHODS.NEXTDAY);
        fulfillmentIdforQtyChange = FULFILLMENT_METHODS.NEXTDAY;
        maxQuantity = Math.min(maxQuantityCheck(), 99);
      } else if (
        fulfillmentId === FULFILLMENT_METHODS.NEXTDAY &&
        nddQuantity < quantity &&
        sthQuantity > nddQuantity
      ) {
        setFulfillmentId?.(FULFILLMENT_METHODS.ONLINEORDER);
        maxQuantity = Math.min(maxQuantityCheck(true, FULFILLMENT_METHODS.ONLINEORDER), 99);
        fulfillmentIdforQtyChange = FULFILLMENT_METHODS.ONLINEORDER;
      } else if (
        sisterStoreAvailable &&
        data.fulfillmentId === FULFILLMENT_METHODS.STOREORDER &&
        !isSisterStoreFlyout
      ) {
        maxQuantity = 0;
      } else {
        maxQuantity = Math.min(maxQuantityCheck(), 99);
      }
      const maxQuantityReached = quantity > maxQuantity;
      const adjustedQuantityForNegative = quantity < 0 ? count : quantity;
      const adjustedQuantity = maxQuantityReached ? maxQuantity : adjustedQuantityForNegative;

      setIsIncOrDecBtnTouched(true);

      if (!disableRemoveButton) {
        updateRequestingOrderItem?.();
      }

      setCount(adjustedQuantity);
      setQuantityEntered(quantity === 0 ? 1 : quantity);

      const showQuantityWarningMessage = true;
      if (count) {
        handleQuantityChange?.(
          adjustedQuantity,
          false,
          showQuantityWarningMessage,
          fulfillmentIdforQtyChange,
        );
      }
      const isQuantityBelowMax = !maxQuantityReached;
      if (isQuantityBelowMax) {
        updateTotalQuantity?.(quantity);
        quantityErrorCheck(false, quantity);
      }
    },
    [
      availabilityCheck,
      fulfillmentId,
      count,
      maxQuantityCheck,
      setFulfillmentId,
      sisterStoreAvailable,
      data,
      isSisterStoreFlyout,
      disableRemoveButton,
      updateRequestingOrderItem,
      handleQuantityChange,
      updateTotalQuantity,
      quantityErrorCheck,
    ],
  );

  const removePart = useCallback(async () => {
    const removeCartAnalyticsData: RemoveCartAnalyticsData = {
      prodSku: data.productInfo.skuId,
      productFulfillmentType: data.orderType
        ? FULFILLMENT_METHODS[data.orderType as keyof typeof FULFILLMENT_METHODS].toString()
        : undefined,
      originalPartType: data.productInfo.originalPartType,
      productRepositoryId: data.productInfo.productId,
      brand: data.productInfo.brand,
      price: data.lineItemPriceInfo?.retailPrice,
      quantity: data.itemQuantity,
      pageName: isMini ? 'miniCart' : 'az:cart:full-cart',
      pageType: isMini ? 'cart' : 'cart',
      siteSection: isMini ? 'miniCart' : 'cart-and-checkout',
    };
    trackCartRemoval(removeCartAnalyticsData, true);
    const certonaOptions = {
      skuIds: data.productInfo.skuId,
      certonaType: certonaPageType.removeFromCartEvent,
    };
    await callToCertona(certonaOptions);
  }, [data, isMini]);

  useEffect(() => {
    if (isMini) {
      updatedValue?.(count);
    }
  }, [count, isMini, updatedValue]);

  useEffect(() => {
    quantityValidityCheck();
    props.updateTotalQuantity?.(count, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!prevProps) {
      return;
    }

    const fulfillmentIdChanged = prevProps.fulfillmentId !== fulfillmentId;
    if (
      prevProps.data.itemQuantity !== data.itemQuantity ||
      prevProps.availabilityCheck !== availabilityCheck ||
      prevProps.data.orderType !== data.orderType ||
      fulfillmentIdChanged
    ) {
      quantityValidityCheck();
      setCount(data.itemQuantity);

      if (
        prevProps.data.orderType !== data.orderType ||
        fulfillmentIdChanged ||
        (prevProps.data.itemQuantity !== 1 && data.itemQuantity === 1)
      ) {
        setQuantityEntered(1);
      }
    }
  }, [
    data.itemQuantity,
    availabilityCheck,
    data.orderType,
    fulfillmentId,
    prevProps,
    quantityValidityCheck,
  ]);

  const pageType = props.pageType;
  const className = props.className;
  const disabled = props.disabled;
  const isNewMessagingShown = props.isNewMessagingShown ?? true;

  const extraMargin = pageType === cartConstants.productDetails;
  const bopusQuantity = availabilityCheck.bopusQuantity ?? 0;
  const nddQuantity = availabilityCheck.nddQuantity ?? 0;
  const sthQuantity = availabilityCheck.sthQuantity ?? 0;
  const sddQuantity = availabilityCheck.sddQuantity ?? 0;
  const rootClass = className ? `${styles.counterpart} ${className}` : styles.counterpart;
  let overQuantity = false;
  let overQuantityv2 = false;
  const shouldFallbackToOnlineOrder =
    fulfillmentId === FULFILLMENT_METHODS.NEXTDAY && nddQuantity < count && sthQuantity >= count;

  const effectiveFulfillmentId = shouldFallbackToOnlineOrder
    ? FULFILLMENT_METHODS.ONLINEORDER
    : fulfillmentId!;

  const availableQuantityForSelectedFulfillment = selectedFulfilmentQuantity(
    effectiveFulfillmentId,
    bopusQuantity,
    sthQuantity,
    sddQuantity,
    nddQuantity,
  );

  useEffect(() => {
    if (shouldFallbackToOnlineOrder) {
      setFulfillmentId?.(FULFILLMENT_METHODS.ONLINEORDER);
    }
  }, [
    fulfillmentId,
    nddQuantity,
    count,
    sthQuantity,
    setFulfillmentId,
    shouldFallbackToOnlineOrder,
  ]);

  overQuantityv2 = quantityEntered > Math.min(availableQuantityForSelectedFulfillment, 99);

  const unitPrice = calculateItemUnitPrice({
    discountedStrikeThroughPrice,
    hasDealId,
    productTotalPrice,
    data,
    strikeThroughPricingEnabled,
    filterproduct,
  });

  const disableIncrementBtn = isSisterStoreFlyout
    ? quantityEntered >= Math.min(availableQuantityForSelectedFulfillment, 99)
    : quantityEntered > Math.min(availableQuantityForSelectedFulfillment, 99);

  if (data.orderType === cartConstants.STOREORDER) {
    const maxBopusQty = Math.min(bopusQuantity, 99);
    overQuantity = quantityEntered > maxBopusQty;
  } else if (data.orderType === cartConstants.ONLINEORDER) {
    const maxSTHQty = sthQuantity > 99 ? 99 : sthQuantity;
    const maxSDDQty = sddQuantity > 99 ? 99 : sddQuantity;
    const maxNDDQty = nddQuantity > 99 ? 99 : nddQuantity;

    if (fulfillmentId === FULFILLMENT_METHODS.SAMEDAY) {
      overQuantity = quantityEntered > maxSDDQty;
    } else if (fulfillmentId === FULFILLMENT_METHODS.NEXTDAY) {
      overQuantity = quantityEntered > maxNDDQty;
    } else {
      overQuantity = quantityEntered > maxSTHQty;
    }
  } else if (data.eligibleForNextDay) {
    overQuantity = quantityEntered > nddQuantity;
  }

  if (props.variant === 'calm') {
    return (
      <div className={rootClass}>
        <div className={azCommonStyles['az-align-center']}>
          <CalmBoxStepper
            buttonGroupClassName={props.stepperButtonClassName}
            count={count}
            minQuantity={1}
            disabled={disabled}
            disableIncrementBtn={disableIncrementBtn}
            hasError={isNewMessagingShown && !isSisterStoreFlyout ? false : overQuantity}
            handleZeroInput={removePart}
            onInputChange={handleChange}
            onIncrementDecrement={handleChange}
            productName={data.productInfo.name}
            isMaxQtyReached={overQuantity}
            unitPrice={unitPrice}
            allow0Input={false}
            ariaDescribedBy={props.ariaDescribedBy}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      {!isNewMessagingShown && !isSisterStoreFlyout && (
        <MiniCartError
          overQuantity={overQuantityv2}
          touched={isIncOrDecBtnTouched}
          label={labels.label_cart_lineItem_MaximumQuantityReached}
        />
      )}
      <div className={azCommonStyles['az-align-center']}>
        <Quantity
          onInputChange={handleChange}
          onIncrementDecrement={handleChange}
          // @ts-expect-error validate and fix type error
          count={count ? parseInt(count) : 1}
          minQuantity={1}
          disableIncrementBtn={disabled || disableIncrementBtn}
          disabled={disabled}
          hasError={isNewMessagingShown && !isSisterStoreFlyout ? false : overQuantity}
          moreMargin={extraMargin}
          isIncOrDecBtnTouched={isIncOrDecBtnTouched}
          handleZeroInput={removePart}
          filterproduct={filterproduct}
          productName={data.productInfo.name}
        />
        <EachItemPrice
          count={count}
          unitPrice={unitPrice}
          isBopusMexicoStoreEnabled={isBopusMexicoStoreEnabled ?? false}
        />
        {!isNewMessagingShown && isSisterStoreFlyout && (
          <MiniCartError
            overQuantity={overQuantityv2}
            touched={isIncOrDecBtnTouched}
            label={labels.label_cart_lineItem_MaximumQuantityReached}
          />
        )}
      </div>
    </div>
  );
};

export default PreOrderV2;
